<template lang="pug">
.gym-page
  .gym-page-layout
    .gym-filter-panel(:class="{collapsed: collapseFilters}")
      gym-filters(
        :useMiles="useMiles"
        :featuredOnly="featuredOnly"
        :dropInsOnly="dropInsOnly"
        :selectedGymType="selectedGymType"
        :activeSearchText="query"
        :gyms="filteredGyms"
        @toggleFeatured="toggleFeatured()"
        @toggleDropIns="toggleDropIns()"
        @updateGymType="updateGymType($event)"
        @search="search($event)"
        :sessionToken="mapboxSessionToken"
        :collapseFilters="collapseFilters"
        @toggleCollapse="onToggleCollapse()"
      )
      .gym-results
        loading(v-if="loadingGyms")
        gym-card(v-else v-for="gym in gymResults" :gym="gym" @viewGymDetails="viewGymDetails(gym._id, [gym.longitude, gym.latitude])")
        RogueButton.show-more(v-if="gymResults.length < visibleResults.length && !loadingGyms" color="link-black" @click="showMore()") {{ __('Show More') }}
        .no-results(v-if="gymResults.length === 0 && !loadingGyms") {{ __('No Results') }}
      .mobile-add-gym
        RogueButton.add-gym-button(@click="openAddGymDrawer()" width="full") {{ __('Add Your Gym') }}
    gym-map(ref="gymMapComponent" @viewGymDetails="viewGymDetails($event)" @addGym="openAddGymDrawer()" :defaultPosition="defaultPosition" :collapseFilters="collapseFilters")
  Drawer.gym-detail-drawer(v-if="route.query.gym" :title="__('View Gym Details')" @hide="hideGymDetails()" :hideCloseButton="!!featuredProduct")
    template(#header)
      template(v-if="featuredProduct")
        button.a11y.back(@click='featuredProduct = null' :aria-label="__('Close')")
          Icon(name='arrow-left')
        h3.title {{ __('Rogue Equipment') }}
      h3(v-else) {{__('View Gym Details')}}
    .featured-product(v-if="featuredProduct")
      ProductSummary(:productId="featuredProduct.productId" :addPadding="true")
    GymDetails(v-else @featureProduct="viewProduct($event)")
  AddGymDrawer(v-if="showAddGymDrawer" @close="showAddGymDrawer = false")
  Drawer(v-if="showLoginDrawer" @hide="showLoginDrawer = false" title="Log In or Create Account" )
    DrawerContents
      Message.login-message(status="warning" icon="guest" iconColor="black")
        .login-message-text {{ __('To add your gym to the Rogue Equipped Gyms Map, please log in or create an account.') }}
      component(
        :is="showRegister ? 'register' : 'login'"
        :extended="false"
        :success-fn="openAddGymDrawer"
      )
        .switch-type
          | {{showRegister ? __('Already have an account?') : __('New Customer?')}}
          .link(@click="showRegister = !showRegister") {{ showRegister ? __('Sign In') : __('Create an Account')}}
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import type { IGym, IGymProduct } from '@rogue/universal'
import AddGymDrawer from './AddGymDrawer.vue'
import GymCard from './GymCard.vue'
import GymDetails from './GymDetails.vue'
import GymFilters from './GymFilters.vue'
import { calculateDistance, formatGymAddress } from './gymHelpers'
import GymMap from './GymMap.vue'
import type { Suggestion } from './gymTypes'
import { messages } from '@/js/app-bridge/messages'
import metadata from '@/js/helpers/metadata'
import { useRogue } from '@/js/services/Rogue'
import { useGymStore } from '@/js/stores/gymStore'
import { useCustomerStore } from '@/js/stores/customerStore'

type MapBounds = {
  minLng : number,
  minLat : number,
  maxLng : number,
  maxLat : number,
}

const SITES = [{
  id       : 1,
  code     : 'USA',
  routeKey : 'US',
  hostName : 'www.roguefitness.com',
}, {
  id       : 4,
  code     : 'APO',
  routeKey : 'US',
  hostName : 'www.roguefitness.com/apo',
}, {
  id       : 2,
  code     : 'CAN',
  routeKey : 'CANADA',
  hostName : 'www.roguecanada.ca',
}, {
  id       : 5,
  code     : 'EUR',
  routeKey : 'EUROPE',
  hostName : 'www.roguefitness.com/eu',
}, {
  id       : 8,
  code     : 'GBR',
  routeKey : 'UK',
  hostName : 'www.roguefitness.com/gb',
}, {
  id       : 18,
  code     : 'AUS',
  routeKey : 'AUSTRALIA',
  hostName : 'www.rogueaustralia.com.au',
}]

const customerStore = useCustomerStore()
const gymStore = useGymStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()

useHead(metadata({ title: rogue.__('Rogue Equipped Gyms') }, rogue))

const mapboxSessionToken = 'RogueMapboxSessionId'

const gymMapComponent = ref<typeof GymMap>()

// This is all of the gyms in our system with no filters or sorting applied
const allGyms = ref<(IGym & { formattedAddress?: string, distance?: number })[]>([])
// This is the list of gyms with the gym type and verified filters applied, sorted by distance
// from the center of map/location search coordinate at the time of the last search execution.
// Product/Gym name filter not applied so that suggestions will include results outside that filter.
const filteredGyms = ref<(IGym & { formattedAddress?: string, distance?: number })[]>([])
// Filtered gym results with Product/Gym filter also applied. This is the list that will show on map.
const mapResults = ref<(IGym & { formattedAddress?: string, distance?: number })[]>([])
// Same as mapResults, but filtered to only include gyms currently visible within the map box area.
const visibleResults = ref<(IGym & { formattedAddress?: string, distance?: number })[]>([])
// Same as visibleResults, but sliced to only include first 25 matches for the results panel
const gymResults = ref<(IGym & { formattedAddress?: string, distance?: number })[]>([])

const query = ref<string>('')
const featuredOnly = ref(false)
const dropInsOnly = ref(false)
const selectedGymType = ref('All Rogue Gyms')
const loadingGyms = ref(true)
const maxResultCount = ref(25)
const featuredProduct = ref<IGymProduct | null>(null)
const showAddGymDrawer = ref(false)
const showLoginDrawer = ref(false)
const showRegister = ref(false)
const collapseFilters = ref(false)

let customQuery: string | null = null
let selectedGymId: string | null = null
let selectedProductId: number | null = null
const defaultPosition = computed(() => {
  switch (rogue.site.code) {
    case 'AUS':
      return [134.93057753675154, -24.837390120573776]
    case 'CAN':
      return [-102.20255976895734, 57.501865910589856]
    case 'EUR':
      return [4.355672640522294, 50.84740205499187]
    case 'GBR':
      return [-2.90431642438405, 55.04836271572965]
    case 'USA':
    case 'APO':
    default:
      return [-82.99015100232816, 39.98545160256798]
  }
})
let mapPosition: number[] = defaultPosition.value

const loggedIn = computed(() => customerStore.loggedIn)

const useMiles = ['USA', 'GBR', 'APO'].includes(rogue.site.code)

function applyFilters() {
  allGyms.value.forEach(gym => {
    gym.distance = calculateDistance(
      mapPosition[1], // latitude
      mapPosition[0], // longitude
      gym.latitude!,
      gym.longitude!,
      useMiles
    )
  })
  maxResultCount.value = 25
  filteredGyms.value = allGyms.value
    .filter(gym => !featuredOnly.value || (featuredOnly.value && gym.isFeatured))
    .filter(gym => !dropInsOnly.value || (dropInsOnly.value && gym.allowDropIn))
    .filter(gym => selectedGymType.value === 'All Rogue Gyms' || selectedGymType.value === gym.type)
    .sort((a, b) => a.distance! - b.distance!)

  mapResults.value = filteredGyms.value
    // @ts-expect-error _id not in ts definition
    .filter(gym => !selectedGymId || gym._id === selectedGymId)
    .filter(gym => !selectedProductId || gym.products?.some(p => p.productId === selectedProductId))
    .filter(gym => !customQuery
      || gym.name.toLowerCase().includes(customQuery.toLowerCase())
      || gym.formattedAddress?.toLowerCase().includes(customQuery.toLowerCase())
      || gym.products?.some(p => p.name.toLowerCase().includes(customQuery!.toLowerCase())))

  gymMapComponent.value?.updateData(mapResults.value)
  updateGymResults()
}

function setGymData(gyms: IGym[]) {
  allGyms.value = gyms
  visibleResults.value = gyms.slice(0, maxResultCount.value)
  gymResults.value = gyms.slice(0, maxResultCount.value)
  applyFilters()
  loadingGyms.value = false
}

async function loadGyms() {
  const gyms = await gymStore.loadAll(rogue) as (IGym & { distance?: number, formattedAddress?: string })[]

  gyms.filter(gym => !gym.shareAddress).forEach(gym => {
    gym.formattedAddress = formatGymAddress(gym.address)
  })
  gymMapComponent.value?.updateData(gyms)

  setGymData(gyms)
}

function onToggleCollapse() {
  collapseFilters.value = !collapseFilters.value
  setTimeout(() => {
    gymMapComponent.value?.resizeMap()
  }, 150)
}

function expandFilters() {
  if (collapseFilters.value) {
    onToggleCollapse()
  }
}

onMounted(() => {
  loadGyms()
})

function viewGymDetails(gymId: string, center?: number[]) {
  rogue.track.track({ type: 'custom', event: 'viewGymDetails', data: { gymId } })
  if (center) {
    gymMapComponent.value?.centerMap(center)
  }
  const newQuery = Object.assign({}, route.query, { gym: gymId })
  router.replace({ query: newQuery })
}

function hideGymDetails() {
  const newQuery = Object.assign({}, route.query, { gym: undefined })
  router.replace({ query: newQuery })
  featuredProduct.value = null
}

async function performLocationSearch(suggestion: Suggestion) {
  const mapboxId = suggestion.uniqueId
  const accessToken = rogue.keys.mapbox?.searchBoxApi
  // Documentation for types returned by mapbox: https://docs.mapbox.com/api/search/search-box/
  try {
    const resp = await rogue.fetch<any>(`https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?access_token=${accessToken}&session_token=${mapboxSessionToken}&language=${rogue.l10n.lang}`, 'get')
    if (resp.features.length) {
      const feature = resp.features[0]
      mapPosition = feature.geometry.coordinates as number[]
      const bbox: number[] | undefined = feature.properties.bbox
      if (bbox) {
        gymMapComponent.value?.fitBounds(bbox)
      } else {
        gymMapComponent.value?.centerMap(mapPosition)
      }
    }
  } catch (e: any) {
    rogue.log.error('Unable to retrieve mapbox location for gym search', e)
    query.value = ''
  }
}

async function search(search: string | Suggestion) {
  expandFilters()
  rogue.track.track({ type: 'custom', event: 'performGymSearch', data: { searchParams: search } })
  loadingGyms.value = true
  selectedGymId = null
  selectedProductId = null
  customQuery = null
  if (typeof search === 'string') {
    query.value = search
    mapPosition = gymMapComponent.value?.getMapCenter() || defaultPosition.value
    if (search) {
      customQuery = search
    }
  } else {
    query.value = search.text
    if (search.type === 'Location') {
      await performLocationSearch(search)
    } else if (search.type === 'Rogue Product') {
      mapPosition = gymMapComponent.value?.getMapCenter() || defaultPosition.value
      selectedProductId = search.uniqueId as number
    } else if (search.type === 'Gym') {
      // @ts-ignore
      const selectedGym: IGym = allGyms.value.find(gym => gym._id === search.uniqueId as string)
      mapPosition = [selectedGym.longitude!, selectedGym.latitude!]
      gymMapComponent.value?.centerMap(mapPosition)
      selectedGymId = search.uniqueId as string
    }
  }
  applyFilters()
  loadingGyms.value = false
}

function toggleFeatured() {
  rogue.track.track({ type: 'custom', event: 'filterFeaturedGyms', data: { featuredOnly: !featuredOnly.value } })
  featuredOnly.value = !featuredOnly.value
  applyFilters()
}

function toggleDropIns() {
  rogue.track.track({ type: 'custom', event: 'filterDropInEnabledGyms', data: { dropInsOnly: !dropInsOnly.value } })
  dropInsOnly.value = !dropInsOnly.value
  applyFilters()
}

function updateGymType(type: string) {
  rogue.track.track({ type: 'custom', event: 'filterGymType', data: { type } })
  selectedGymType.value = type
  applyFilters()
}

function updateGymResults() {
  const mapBoundsSet: boolean = gymMapComponent.value?.bounds.minLat
    || gymMapComponent.value?.bounds.maxLat
    || gymMapComponent.value?.bounds.minLng
    || gymMapComponent.value?.bounds.maxLng

  visibleResults.value = mapResults.value
    .filter(gym => !(gym.latitude && gym.longitude) || !mapBoundsSet
      || (gym.latitude >= gymMapComponent.value?.bounds.minLat
      && gym.latitude <= gymMapComponent.value?.bounds.maxLat
      && gym.longitude >= gymMapComponent.value?.bounds.minLng
      && gym.longitude <= gymMapComponent.value?.bounds.maxLng))
    .sort((a, b) => a.distance! - b.distance!)
  gymResults.value = visibleResults.value.slice(0, maxResultCount.value)
}

function showMore() {
  rogue.track.track({ type: 'custom', event: 'showMoreGymResults', data: { resultCount: maxResultCount.value } })
  maxResultCount.value += 25
  updateGymResults()
}

function openAddGymDrawer() {
  if (loggedIn.value) {
    showLoginDrawer.value = false
    showAddGymDrawer.value = true
  } else {
    rogue.track.track({ type: 'custom', event: 'openAddGymDrawer', data: {} })
    if (rogue.isApp()) {
      rogue.emitToApp(messages.LOGIN, { redirect: route.fullPath })
    } else {
      showLoginDrawer.value = true
    }
  }
}

const currentSite = SITES.find(s => s.code === rogue.site.code)!
async function viewProduct(product: IGymProduct) {
  rogue.track.track({ type: 'custom', event: 'viewGymProduct', data: { product } })
  if (product.storeId === rogue.site.defaultStoreId) {
    featuredProduct.value = product
  } else {
    // If this product is from another site, do a best guess as finding which page to take the user to
    // (either a related product on this site or a category page)
    const site = SITES.find(s => s.id === product.storeId)!
    try {
      const resp = await rogue.fetch<any>(`svc_route/site/${site?.code}/route?url=${product.url}`)
      if (resp.related?.[currentSite.routeKey]) {
        router.push(resp.related[currentSite.routeKey])
      } else {
        window.open(`https://${site.hostName}${product.url}`, '_blank')
      }
    } catch (e: any) {
      window.open(`https://${site.hostName}${product.url}`, '_blank')
      rogue.log.error('Unable to find related routes for product', e, product)
    }
  }
}

watch(() => gymMapComponent.value?.bounds, (newBounds: MapBounds) => {
  if (newBounds && Object.values(newBounds).some(b => !!b)) {
    const center = gymMapComponent.value?.getMapCenter()
    allGyms.value.forEach(gym => {
      gym.distance = calculateDistance(
        center[1], // latitude
        center[0], // longitude
        gym.latitude!,
        gym.longitude!,
        useMiles
      )
    })

    updateGymResults()
  }
}, {
  deep: true,
})

</script>
<style lang="scss" scoped>
.gym-page-layout {
  background-color: $color-black-light;
  display: flex;
  height: 900px;

  .gym-filter-panel {
    display: flex;
    flex-direction: column;
    max-width: 380px;
    width: 100%;
    z-index: 1;

    .mobile-add-gym {
      background-color: $color-white;
      box-shadow: 0px -2px 8px 0px $color-gray-light;
      height: 70px;
      display: none;
      padding: $space-xs $space-s;

      @include not-desktop() {
        display: block;
      }
    }

    .gym-results {
      background-color: $color-white;
      overflow-y: auto;
      flex: 1 1 0px;

      .show-more {
        background: $color-gray-x-light;
      }
    }

    .no-results {
      font-weight: bold;
      padding: $space-s;
      text-align: center;
      text-transform: uppercase;
    }

    @include not-desktop() {
      max-width: 100%;
      flex-shrink: 1;
      flex-grow: 1;
      transition: all 100ms ease-out;

      &.collapsed {
        flex-shrink: 0;
        flex-grow: 0;

        .gym-results {
          display: none;
        }
      }
    }
  }

  @include not-desktop() {
    flex-direction: column-reverse;
    height: auto;
    position: fixed;
    top: 51px;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
  }
}

.login-message {
  margin-bottom: $space-s;

  &-text {
    font-size: $size-xs;
  }
}
</style>

<style lang="scss">
.gym-detail-drawer {
  .back {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    padding: $space-s $space-xs $space-s $space-s;

    &:hover {
      cursor: pointer;
    }
  }

  .title {
    margin-left: 30px;
  }

  .drawer-header {
    gap: $space-xxs;
    justify-content: left;
  }
}

.switch-type {
  font-size: $size-xs;
  text-align: center;
  margin-top: $space-s;

  .link {
    display: inline-block;
    margin-left: $space-xxs;
  }
}

@include not-desktop() {
  footer {
    display: none;
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  .gym-page-layout {
    @include not-desktop() {
      top: 0;
    }
  }
}
</style>
